import React, { useMemo } from 'react';
import { Loader2, Printer } from 'lucide-react';
import { ersthelfer, notfallStellen, notrufLuecken } from '../bestandUebernahme';
import {
    AushangHygiene, AushangNotfall, AushangNotfallausweis, AushangSchleuse,
    AushangSchutzklasse,
} from './Aushaenge';
import { AushangRettungskette } from './AushangRettungskette';
import { notrufZeilen, verfuegbareAushaenge } from './aushangKatalog';
import type { ProjektBestand } from '../useProjektBestand';
import type { AsplanBundle, SchleusenParameter } from '../types';

/**
 * Der Reiter „Aushänge" — Liste, Druckknopf und die Blaetter selbst.
 *
 * ═══ WARUM HIER NICHT MEHR GELADEN WIRD ═══════════════════════════════════
 * Die Notrufdaten gehoeren dem SiGe-Plan und werden von dort GELESEN (Plan
 * Abschnitt 10: „lesen — EINE Quelle"). Dieser Reiter lud sie frueher selbst,
 * neben Notfallkarte und Bestandskarte — drei unabhaengige Staende derselben
 * Quelle. Aendert jemand den Notrufplan parallel im SiGe-Plan, zeigte der
 * Reiter „Grundlagen" einen anderen Stand als das Blatt, das hier GEDRUCKT
 * wird; und jeder Reiterwechsel loeste eine neue Abfrage aus.
 *
 * Gelesen wird jetzt einmal in `AsplanModul.tsx` (`useProjektBestand`), und
 * der Stand kommt als Prop an — der duenne Container haelt den Zustand, die
 * Blaetter zeigen ihn.
 *
 * ═══ WAS HIER NICHT PASSIERT ══════════════════════════════════════════════
 * Nichts wird gespeichert. Klinik, Durchgangsarzt und Ersthelfer wandern
 * NICHT in die asplan-Tabellen — sonst waeren sie ab dem naechsten Wechsel
 * des Durchgangsarztes an zwei Stellen zu pflegen und wuerden an einer
 * gepflegt. Eine veraltete Notfallnummer macht im Ernstfall niemanden
 * stutzig; eine fehlende schon. Deshalb wird eine fehlende Angabe auf dem
 * Blatt BENANNT und nie durch einen Gedankenstrich ersetzt.
 */

export const AushaengeReiter: React.FC<{
    /** Stammdaten und Notrufplan — im Container EINMAL gelesen. */
    bestand: ProjektBestand;
    projektName: string;
    projektNummer?: string;
    bundle: AsplanBundle;
    /** Die Namen der gefuehrten Gefahrstoffe, im Container schon berechnet. */
    stoffe: string[];
    schleuse: SchleusenParameter;
}> = ({ bestand, projektName, projektNummer, bundle, stoffe, schleuse }) => {
    const { bereit, notrufplan } = bestand;
    const notfall = bundle.plan.notfall;

    const stellen = useMemo(() => notfallStellen(notrufplan), [notrufplan]);
    const helfer = useMemo(() => ersthelfer(notrufplan), [notrufplan]);
    const luecken = useMemo(() => notrufLuecken(notrufplan, notfall), [notrufplan, notfall]);
    const zeilen = useMemo(() => notrufZeilen(notrufplan, notfall), [notrufplan, notfall]);
    const liste = useMemo(
        () => verfuegbareAushaenge(bundle, notrufplan), [bundle, notrufplan]);

    // Solange der Notrufplan nicht gelesen ist, wird kein Blatt gezeigt. Jedes
    // dieser Blaetter traegt Notfallnummern; „nicht hinterlegt" waere dann
    // eine Behauptung ueber fehlende Notfalldaten, die niemand geprueft hat —
    // und ein Druck in diesem Moment nimmt sie mit aufs Papier.
    if (!bereit) {
        return (
            <section className="bg-white border border-slate-200 rounded-2xl p-4 mb-4">
                <h2 className="font-bold text-slate-800 mb-0.5">Aushänge</h2>
                <p className="text-sm text-slate-400 py-3 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Notrufplan des SiGe-Plans wird gelesen …
                </p>
            </section>
        );
    }

    return (
        <>
            {/* Nur dieser Bereich wandert in den Druck: Reiterleiste,
                Kennzahlen und Rahmen haben auf einem Aushang nichts zu
                suchen. Die Regel steht bewusst lokal, damit sie nicht
                versehentlich andere Module trifft. */}
            <style>{`
                @media print {
                    body * { visibility: hidden; }
                    #asplan-aushaenge, #asplan-aushaenge * { visibility: visible; }
                    #asplan-aushaenge { position: absolute; inset: 0; }
                }
            `}</style>

            <section className="bg-white border border-slate-200 rounded-2xl p-4 mb-4">
                <h2 className="font-bold text-slate-800 mb-0.5">Aushänge</h2>
                <p className="text-xs text-slate-500 mb-3">
                    Große Fläche, große Schrift, Piktogramme: jedes Blatt muss ohne den Plan
                    verständlich sein. Wer davorsteht, hat das Dokument nicht in der Hand.
                    Klinik, Durchgangsarzt und Ersthelfer kommen aus dem Notrufplan des
                    SiGe-Plans und werden dort gepflegt, nicht hier.
                </p>

                <ul className="space-y-1.5 mb-3">
                    {liste.map(x => (
                        <li key={x.key} className="text-sm flex items-start gap-2">
                            <span className="text-slate-400">·</span>
                            <span>
                                <b className="text-slate-800">{x.titel}</b>
                                <span className="block text-xs text-slate-500">Aushängen: {x.ort}</span>
                                {x.fehlt && (
                                    <span className="block text-xs text-amber-700 mt-0.5">{x.fehlt}</span>
                                )}
                            </span>
                        </li>
                    ))}
                </ul>
                <button
                    type="button"
                    onClick={() => window.print()}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-stone-700 text-white rounded-lg text-xs font-bold"
                >
                    <Printer className="w-3.5 h-3.5" /> Alle Aushänge drucken
                </button>
            </section>

            <div id="asplan-aushaenge" data-glass-papier>
                <AushangNotfall
                    projektName={projektName}
                    stoffe={stoffe}
                    notrufnummern={zeilen}
                    sammelplatz={String(notfall.sammelplatz ?? '')}
                    luecken={luecken}
                />

                <AushangRettungskette
                    projektName={projektName}
                    stellen={stellen}
                    helfer={helfer}
                    kartenBild={notrufplan?.kartenBild}
                />

                <AushangSchleuse projektName={projektName} parameter={schleuse} />

                <AushangHygiene projektName={projektName} />

                {bundle.cluster.filter(c => c.art !== 'abgegrenzt').map(c => {
                    const klasse = bundle.schutzklassen.find(k => k.key === c.schutzklasse_key);
                    if (!klasse) return null;
                    return (
                        <AushangSchutzklasse
                            key={c.id}
                            projektName={projektName}
                            bereich={c.kennung}
                            bereichName={c.bezeichnung}
                            felder={c.felder}
                            klasse={klasse}
                        />
                    );
                })}

                <AushangNotfallausweis
                    projektName={projektName}
                    projektNummer={projektNummer}
                    stoffe={stoffe}
                    giftnotruf={String(notfall.giftnotruf ?? '')}
                    koordinator={String(notfall.koordinator_tel ?? '')}
                    stellen={stellen}
                />
            </div>
        </>
    );
};
